import { fractionToPercentage } from './number';

export const DEFAULT_BONUS = 'R$ 500';
export const DEFAULT_BADGE = 'em bônus na 1ª compra';
export const DEFAULT_DURATION_MINUTES = 24 * 60; // 1440 min = 24h
export const DEFAULT_DURATION_MS = DEFAULT_DURATION_MINUTES * 60 * 1000;

/**
 * Nome da flag no Unleash (app: lojas-web, environment: development).
 */
export const UNLEASH_PROMO_FLAG = 'promoFlagEnabled';

export const isPromoUnleashEnabled = unleashFlag => Boolean(unleashFlag);

export const parseBonusAmount = value =>
  typeof value === 'string' && value.trim() ? value.trim() : DEFAULT_BONUS;

/** Converte minutos (homeCouponBannerTimer) em ms para o contador */
export const parseDurationMsFromMinutes = durationMinutes => {
  const minutes = Number(durationMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return DEFAULT_DURATION_MS;
  }
  return minutes * 60 * 1000;
};

export const formatCouponBonusAmount = couponValue => {
  const amount = Number(couponValue);
  if (!Number.isFinite(amount) || amount <= 0) {
    return DEFAULT_BONUS;
  }

  const formatted = Number.isInteger(amount)
    ? String(amount)
    : amount.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

  return `R$ ${formatted}`;
};

/** Desembrulha corpo HTTP (authGet retorna resposta axios em .data). */
export const unwrapApiResponse = response => {
  if (!response || response.responseStatus) {
    return null;
  }

  const root = response.data ?? response;

  return root?.body?.data ?? root?.body ?? root?.data?.data ?? root?.data ?? root;
};

/**
 * Extrai do GET time-benefits-club os campos usados pelo banner.
 */
export const parseTimeBenefitsBannerResponse = response => {
  const raw = unwrapApiResponse(response) ?? {};

  const timerMinutes = Number(raw.homeCouponBannerTimer);
  const validationHours = Number(raw.firstLoginTimeValidationInHours);
  const couponValue = Number(raw.couponValue);

  return {
    homeCouponBannerTimer:
      Number.isFinite(timerMinutes) && timerMinutes > 0
        ? timerMinutes
        : DEFAULT_DURATION_MINUTES,
    firstLoginValidationHours:
      Number.isFinite(validationHours) && validationHours > 0 ? validationHours : null,
    couponValue: Number.isFinite(couponValue) && couponValue > 0 ? couponValue : null,
  };
};

/**
 * Converte minutos (homeCouponBannerTimer) para exibição em horas no texto (ex.: 1440 → "24").
 */
export const formatDurationHoursForDisplay = durationMinutes => {
  const minutes = Number(durationMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '24';
  }

  const hours = minutes / 60;
  if (Number.isInteger(hours)) {
    return String(hours);
  }

  const rounded = Math.round(hours * 10) / 10;
  return String(rounded).replace('.', ',');
};

/**
 * Payload Unleash opcional (JSON no variant): { "bonusAmount": "R$ 500" }
 */
export const parseUnleashPromoPayload = variant => {
  const fallback = { bonusAmount: DEFAULT_BONUS };

  if (!variant?.payload?.value) {
    return fallback;
  }

  try {
    const raw = JSON.parse(variant.payload.value);

    return {
      bonusAmount: parseBonusAmount(raw.bonusAmount ?? raw.bonus ?? raw.valor),
    };
  } catch {
    return fallback;
  }
};

export const parseTimestamp = value => {
  if (value == null || value === '') {
    return null;
  }

  const ts = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
};

const pickFirstLoginAt = source => {
  if (!source) {
    return null;
  }

  return (
    source.firstLoginAt ??
    source.first_login_at ??
    source.firstLoginDate ??
    source.first_login_date ??
    source.firstLogin ??
    source.first_login ??
    source.profile?.firstLoginAt ??
    source.profile?.first_login_at ??
    source.dealer?.firstLoginAt ??
    null
  );
};

/** Normaliza resposta do GET ic-api-dealers/v1/dealers/:id */
export const parseDealerApiResponse = response => {
  const raw = unwrapApiResponse(response);
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const nested = raw.data;
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    (nested._id || nested.id || nested.firstLoginAt || nested.createdAt)
  ) {
    return nested;
  }

  return raw;
};

/** Data/hora de cadastro na raiz do dealer (createdAt). */
export const resolveDealerRegisteredAt = (...sources) => {
  for (const source of sources) {
    if (!source) {
      continue;
    }

    const value = source.createdAt ?? source.created_at ?? null;
    if (value) {
      return value;
    }
  }

  return null;
};

/** Início do contador — GET ic-api-dealers/v1/dealers/:id (firstLoginAt). */
export const resolvePromoBannerStartAt = (...sources) => {
  for (const source of sources) {
    const value = pickFirstLoginAt(source);
    if (value) {
      return value;
    }
  }
  return null;
};

/**
 * Referência (cadastro) dentro da janela firstLoginTimeValidationInHours (horas na API).
 */
export const isWithinPromoValidationWindow = ({
  windowStartAt,
  validationHours,
  now = Date.now(),
}) => {
  const startTimestamp = parseTimestamp(windowStartAt);
  if (!startTimestamp) {
    return false;
  }

  const hours = Number(validationHours);
  if (!Number.isFinite(hours) || hours <= 0) {
    return true;
  }

  const elapsedMs = now - startTimestamp;
  return elapsedMs >= 0 && elapsedMs <= hours * 60 * 60 * 1000;
};

/** @deprecated use isWithinPromoValidationWindow */
export const isFirstLoginWithinValidationWindow = params =>
  isWithinPromoValidationWindow(params);

/** Precisa consultar my-purchases (cadastro na janela + createdAt + config). */
export const shouldFetchPurchasesForPromo = ({ dealer, timeConfig, now = Date.now() }) => {
  const registeredAt = resolveDealerRegisteredAt(dealer);
  const validationHours = timeConfig?.firstLoginValidationHours;

  return Boolean(
    registeredAt &&
    validationHours &&
    isWithinPromoValidationWindow({
      windowStartAt: registeredAt,
      validationHours,
      now,
    })
  );
};

/**
 * Contador: agora − (firstLoginAt + homeCouponBannerTimer).
 * Início = 1º login; fim = início + timer do cupom (minutos).
 */
export const computePromoBannerCountdown = ({
  startAt,
  countdownEndsAt,
  durationMs = DEFAULT_DURATION_MS,
  now = Date.now(),
}) => {
  let startTimestamp = parseTimestamp(startAt);
  let endTimestamp = parseTimestamp(countdownEndsAt);

  if (endTimestamp == null && startTimestamp != null) {
    endTimestamp = startTimestamp + durationMs;
  }

  if (startTimestamp == null && endTimestamp != null && durationMs > 0) {
    startTimestamp = endTimestamp - durationMs;
  }

  if (startTimestamp == null || endTimestamp == null) {
    return {
      startTimestamp: null,
      endTimestamp: null,
      totalDurationMs: durationMs,
      remainingMs: 0,
      elapsedMs: 0,
      progress: 0,
      isActive: false,
    };
  }

  const totalDurationMs = Math.max(0, endTimestamp - startTimestamp);
  const elapsedMs = Math.min(totalDurationMs, Math.max(0, now - startTimestamp));
  const remainingMs = Math.max(0, endTimestamp - now);
  const progress =
    totalDurationMs > 0
      ? fractionToPercentage(remainingMs / 1000, totalDurationMs / 1000)
      : 0;

  return {
    startTimestamp,
    endTimestamp,
    totalDurationMs,
    remainingMs,
    elapsedMs,
    progress,
    isActive: now >= startTimestamp && remainingMs > 0,
  };
};

/** Formato de data aceito por GET my-purchases (dateStart / dateEnd). */
export const formatPurchasesApiDate = dateInput => {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

export const getValidationWindowEndTimestamp = ({ windowStartAt, validationHours }) => {
  const startTimestamp = parseTimestamp(windowStartAt);
  if (!startTimestamp) {
    return null;
  }

  const hours = Number(validationHours);
  if (!Number.isFinite(hours) || hours <= 0) {
    return null;
  }

  return startTimestamp + hours * 60 * 60 * 1000;
};

/**
 * Query my-purchases: dateStart = dia do createdAt; dateEnd = createdAt + validationHours.
 * Ex.: dateStart=18/05/2026&dateEnd=27/05/2026
 */
export const buildPurchasesQueryDateRange = ({ createdAt, validationHours }) => {
  const dateStart = formatPurchasesApiDate(createdAt);
  const windowEnd = getValidationWindowEndTimestamp({
    windowStartAt: createdAt,
    validationHours,
  });

  if (!dateStart || !windowEnd) {
    return null;
  }

  return {
    dateStart,
    dateEnd: formatPurchasesApiDate(windowEnd),
  };
};

/** Erro do authGet (catch devolve objeto com responseStatus). */
export const isAuthGetErrorResponse = response => Boolean(response?.responseStatus);

/**
 * Normaliza body do my-purchases.
 * Regra de negócio: body vazio (null, {}, []) = nenhuma compra = sem disputa ganha.
 */
export const parseMyPurchasesList = response => {
  if (isAuthGetErrorResponse(response)) {
    return [];
  }

  if (response == null) {
    return [];
  }

  const root = response.data ?? response;

  if (Array.isArray(root)) {
    return root;
  }

  const body =
    root?.body ??
    root?.data?.body ??
    (Array.isArray(root?.data) ? root.data : undefined) ??
    unwrapApiResponse(response);

  if (Array.isArray(body)) {
    return body;
  }

  if (body == null || typeof body !== 'object') {
    return [];
  }

  if (Array.isArray(body.data)) {
    return body.data;
  }

  if (Array.isArray(body.purchases)) {
    return body.purchases;
  }

  return [];
};

const PURCHASE_STATUS_ON_OFFER = 'ON_OFFER';
const PURCHASE_STATUS_OFFER_ACCEPTED = 'OFFER_ACCEPTED';
const PURCHASE_STATUS_OFFER_DECLINED = 'OFFER_DECLINED';

/** Status que indicam vitória após ON_OFFER (mesma ordem de newFormat STATUS). */
const PURCHASE_STATUSES_AFTER_WIN = new Set([
  'OFFER_ACCEPTED',
  'TICKET_SENT',
  'PAID',
  'BOUGHT_CAR',
  'CAR_DELIVERED_BY_SELLER',
  'WAITING_RELEASE_CAR',
  'WAITING_RELEASE_CAR_BLOCKED',
  'WAITING_RELEASE_CAR_DATE_CHANGE',
  'WAITING_WITHDRAWAL',
  'DELIVERED',
  'DOCUMENTATION_IN_PROGRESS',
  'DOCUMENTATION_READY',
  'RETURNED',
  'CREATED_CAUTELAR',
  'CREATED_ECV',
  'CREATED_RESEARCH',
  'PAID_ENTRADA',
]);

const pickNegotiationStartedAt = purchase =>
  purchase?.negotiationStartedAt ?? purchase?.negotiation_started_at ?? null;

const pickNegotiationStatus = event =>
  event?.status ?? event?.Status ?? event?.state ?? null;

const pickCurrentStatus = purchase =>
  purchase?.currentStatus ?? purchase?.current_status ?? null;

/** Timeline au-queries: negotiations ou foundNegotiations. */
const resolveNegotiationsList = purchase => {
  if (Array.isArray(purchase?.negotiations) && purchase.negotiations.length > 0) {
    return purchase.negotiations;
  }

  if (
    Array.isArray(purchase?.foundNegotiations) &&
    purchase.foundNegotiations.length > 0
  ) {
    return purchase.foundNegotiations;
  }

  return [];
};

const hasWinningBid = purchase => {
  const bid = purchase?.winningBid ?? purchase?.winning_bid;

  if (bid == null || bid === '') {
    return false;
  }

  if (typeof bid === 'number') {
    return bid > 0;
  }

  if (typeof bid === 'object') {
    const amount = Number(bid.amount ?? bid.value ?? bid.price);
    return Number.isFinite(amount) && amount > 0;
  }

  return true;
};

/**
 * Disputa ganha (au-queries / Minhas compras):
 * - currentStatus ON_OFFER ou pós-vitória;
 * - foundNegotiations com OFFER_ACCEPTED, BOUGHT_CAR, PAID, etc.;
 * - só OFFER_DECLINED (sem vitória) → não bloqueia por este critério sozinho.
 */
export const isPurchaseWonDispute = purchase => {
  if (!purchase) {
    return false;
  }

  const currentStatus = pickCurrentStatus(purchase);

  if (
    currentStatus === PURCHASE_STATUS_ON_OFFER ||
    (currentStatus && PURCHASE_STATUSES_AFTER_WIN.has(currentStatus))
  ) {
    return true;
  }

  const negotiations = resolveNegotiationsList(purchase);
  const statuses = negotiations.map(pickNegotiationStatus).filter(Boolean);

  if (statuses.includes(PURCHASE_STATUS_ON_OFFER)) {
    return true;
  }

  if (statuses.some(status => PURCHASE_STATUSES_AFTER_WIN.has(status))) {
    return true;
  }

  if (negotiations.length === 0) {
    return hasWinningBid(purchase) && Boolean(pickNegotiationStartedAt(purchase));
  }

  const onlyDeclined =
    statuses.length > 0 &&
    !statuses.includes(PURCHASE_STATUS_ON_OFFER) &&
    statuses.every(status => status === PURCHASE_STATUS_OFFER_DECLINED);

  if (onlyDeclined) {
    return false;
  }

  return hasWinningBid(purchase) && Boolean(pickNegotiationStartedAt(purchase));
};

/** Body vazio = sem disputa ganha; com itens, bloqueia se algum for disputa ganha. */
export const hasNoWonDisputeFromPurchases = (purchases = []) => {
  if (!Array.isArray(purchases) || purchases.length === 0) {
    return true;
  }

  return !purchases.some(isPurchaseWonDispute);
};

/** Momento em que a disputa foi ganha (negociação iniciada após vitória). */
export const resolveDisputeWonAt = purchase => {
  if (!isPurchaseWonDispute(purchase)) {
    return null;
  }

  const direct = pickNegotiationStartedAt(purchase);
  if (direct) {
    return direct;
  }

  const winningBid = purchase?.winningBid;
  if (winningBid) {
    const fromBid =
      winningBid.createdAt ??
      winningBid.created_at ??
      winningBid.timestamp ??
      winningBid.updatedAt ??
      winningBid.updated_at;
    if (fromBid) {
      return fromBid;
    }
  }

  const negotiations = resolveNegotiationsList(purchase);
  if (negotiations.length > 0) {
    const onOffer = negotiations.find(
      event => pickNegotiationStatus(event) === PURCHASE_STATUS_ON_OFFER
    );
    const event = onOffer ?? negotiations[0];
    const fromEvent =
      event?.updatedAt ??
      event?.updated_at ??
      event?.timestamp ??
      event?.createdAt ??
      event?.created_at;
    if (fromEvent) {
      return fromEvent;
    }
  }

  return (
    purchase?.createdAt ??
    purchase?.created_at ??
    purchase?.auction?.negotiationStartedAt ??
    purchase?.auction?.negotiation_started_at ??
    null
  );
};

/**
 * Quantidade de disputas ganhas na lista (my-purchases já filtra por dateStart/dateEnd).
 */
export const countWonDisputesInValidationWindow = ({
  purchases = [],
}) => purchases.filter(isPurchaseWonDispute).length;

/** true se há disputa ganha na resposta do my-purchases. */
export const hasWonDisputeInValidationWindow = ({
  purchases = [],
}) => countWonDisputesInValidationWindow({ purchases }) > 0;

/** Log no console: resposta au-queries/my-purchases e critério de disputa ganha. */
export const logPromoMyPurchasesDiagnostic = ({
  requestUrl,
  createdAt,
  validationHours,
  response,
  purchases = [],
}) => {
  const dateRange = buildPurchasesQueryDateRange({ createdAt, validationHours });
  const wonDisputesCount = countWonDisputesInValidationWindow({ purchases });

  const root = response?.data ?? response;
  const rawBody = root?.body ?? root?.data?.body ?? root?.data ?? null;

  const items = purchases.map((purchase, index) => {
    const timeline = resolveNegotiationsList(purchase);

    return {
      index,
      id: purchase?.id,
      vsrId: purchase?.vsrId,
      slug: purchase?.slug,
      winningBid: purchase?.winningBid,
      currentStatus: pickCurrentStatus(purchase),
      negotiationStartedAt:
        purchase?.negotiationStartedAt ?? purchase?.negotiation_started_at,
      negotiationsStatuses: timeline.map(pickNegotiationStatus),
      isWonDispute: isPurchaseWonDispute(purchase),
      wonAt: resolveDisputeWonAt(purchase),
    };
  });

  console.group('[PromoBanner] au-queries / my-purchases');
  console.warn('requestUrl:', requestUrl);
  console.warn('Resposta completa (objeto axios inteiro):', response);
  console.warn('response.data:', response?.data);
  console.warn('body (lista Minhas compras):', rawBody);
  console.warn('Análise do banner:', {
    dateStart: dateRange?.dateStart,
    dateEnd: dateRange?.dateEnd,
    createdAt,
    validationHours,
    purchasesCount: purchases.length,
    hasNoWonDispute: hasNoWonDisputeFromPurchases(purchases),
    wonDisputesCount,
    hasWonDisputeInValidationWindow: wonDisputesCount > 0,
    items,
    parsedPurchases: purchases,
  });
  console.groupEnd();
};

/**
 * promoEligible (calculado no front):
 * - tem createdAt na raiz;
 * - cadastro dentro de firstLoginTimeValidationInHours;
 * - my-purchases consultado com sucesso (lista vazia = sem disputa ganha);
 * - sem disputa ganha entre createdAt e fim dessa janela.
 * firstLoginAt só entra no showPromo via contador (countdown.isActive).
 */
export const computePromoEligible = ({
  dealer,
  timeConfig,
  purchases = [],
  purchasesResolved = true,
  purchasesCheckFailed = false,
  skipPurchasesCheck = false,
  now = Date.now(),
}) => {
  if (!skipPurchasesCheck && (!purchasesResolved || purchasesCheckFailed)) {
    return false;
  }

  const registeredAt = resolveDealerRegisteredAt(dealer);
  const validationHours = timeConfig?.firstLoginValidationHours;

  if (!registeredAt) {
    return false;
  }

  if (
    !isWithinPromoValidationWindow({
      windowStartAt: registeredAt,
      validationHours,
      now,
    })
  ) {
    return false;
  }

  // Body vazio = sem disputa; com disputa ganha na lista = não exibe banner.
  return hasNoWonDisputeFromPurchases(purchases);
};

/** Alias usado pelo hook do banner. */
export const isPromoBannerEligible = (dealer, timeConfig, options = {}) =>
  computePromoEligible({ dealer, timeConfig, ...options });

/** Lista motivos pelos quais o banner promo não aparece (dev). */
export const getPromoBannerBlockers = ({
  isLoggedIn,
  flagsReady,
  promoFeatureEnabled,
  dealer,
  dealerError,
  timeConfig,
  countdown,
  purchasesResolved,
  purchasesCheckFailed,
  purchases,
}) => {
  const blockers = [];

  if (!isLoggedIn) {
    blockers.push('usuario_nao_logado');
  }
  if (!flagsReady) {
    blockers.push('unleash_ainda_carregando');
  }
  if (!promoFeatureEnabled) {
    blockers.push('flag_promoFlagEnabled_desligada_no_unleash');
  }
  if (dealerError) {
    blockers.push(`erro_ao_buscar_dealer: ${dealerError}`);
  }

  const registeredAt = resolveDealerRegisteredAt(dealer);
  const firstLoginAt = resolvePromoBannerStartAt(dealer);
  const validationHours = timeConfig?.firstLoginValidationHours;

  if (!registeredAt) {
    blockers.push('dealer_sem_createdAt_na_raiz');
  } else if (!validationHours) {
    blockers.push('config_sem_firstLoginTimeValidationInHours');
  } else if (
    !isWithinPromoValidationWindow({
      windowStartAt: registeredAt,
      validationHours,
    })
  ) {
    blockers.push('cadastro_fora_da_janela_firstLoginTimeValidationInHours');
  } else if (!purchasesResolved) {
    blockers.push('verificacao_de_compras_pendente');
  } else if (purchasesCheckFailed) {
    blockers.push('erro_ao_consultar_my_purchases');
  } else if (!hasNoWonDisputeFromPurchases(purchases ?? [])) {
    blockers.push('venceu_disputa_entre_cadastro_e_fim_da_janela');
  }

  if (!firstLoginAt) {
    blockers.push('dealer_sem_firstLoginAt_contador');
  } else if (!countdown?.isActive) {
    blockers.push('contador_expirado_ou_ainda_nao_comecou');
  }

  return blockers;
};

/** Motivos do fallback IPVA (útil em dev). */
export const getPromoBannerVisibilityChecks = ({
  configLoading,
  dealerLoading,
  purchasesLoading,
  isLoggedIn,
  promoFlagEnabled,
  promoFeatureEnabled,
  flagsReady,
  dealer,
  dealerError,
  purchasesError,
  purchases,
  purchasesResolved,
  purchasesCheckFailed,
  timeConfig,
  countdown,
}) => {
  const registeredAt = resolveDealerRegisteredAt(dealer);
  const firstLoginAt = resolvePromoBannerStartAt(dealer);
  const validationHours = timeConfig?.firstLoginValidationHours;
  const purchasesList = purchases ?? [];
  const needsPurchasesCheck = shouldFetchPurchasesForPromo({ dealer, timeConfig });
  const wonDisputesCount = countWonDisputesInValidationWindow({
    purchases: purchasesList,
  });

  return {
    configLoading,
    dealerLoading,
    purchasesLoading,
    isLoggedIn,
    flagsReady,
    promoFlagEnabled,
    promoFeatureEnabled,
    dealerError,
    purchasesError,
    purchasesResolved: purchasesResolved ?? false,
    purchasesCheckFailed: purchasesCheckFailed ?? false,
    purchasesCount: purchasesList.length,
    hasNoWonDispute: hasNoWonDisputeFromPurchases(purchasesList),
    wonDisputesCount,
    hasWonDisputeInValidationWindow: wonDisputesCount > 0,
    needsPurchasesCheck,
    promoEligible: computePromoEligible({
      dealer,
      timeConfig,
      purchases: purchasesList,
      purchasesResolved: needsPurchasesCheck ? purchasesResolved ?? false : true,
      purchasesCheckFailed: needsPurchasesCheck ? purchasesCheckFailed ?? false : false,
      skipPurchasesCheck: !needsPurchasesCheck,
    }),
    registeredAt,
    firstLoginAt,
    dealerKeys: dealer ? Object.keys(dealer).slice(0, 20) : null,
    validationHours,
    withinValidationWindow: isWithinPromoValidationWindow({
      windowStartAt: registeredAt,
      validationHours,
    }),
    bannerTimerMinutes: timeConfig?.homeCouponBannerTimer,
    countdownActive: countdown?.isActive ?? false,
    remainingMs: countdown?.remainingMs ?? 0,
    endTimestamp: countdown?.endTimestamp ?? null,
    blockers: getPromoBannerBlockers({
      isLoggedIn,
      flagsReady,
      promoFeatureEnabled,
      dealer,
      dealerError,
      timeConfig,
      countdown,
      purchasesResolved,
      purchasesCheckFailed,
      purchases,
    }),
  };
};
